import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { Permission } from '@shared/enums/permission';
import { LayoutComponent } from './modules/layout/layout.component';
import { PermissionGuard } from './guards/permission.guard';
import { initialGuard } from './guards';
import { contentHiddenGuard } from './guards/hide-content.guard';
import { BuildType } from '@shared/enums/build.type';
import { EnvironmentService } from '@services/utils/environment.service';
import { PRIVACY_POLICY_SLUG_PARAM } from '@shared/consts/routes';

const routes: Routes = [
  {
    path: 'autologin',
    loadComponent: () => import('./pages/autologin/autologin.component').then(c => c.AutologinComponent),
  },
  {
    path: 'after-login',
    loadComponent: () => import('./pages/redirect/redirect.component').then(c => c.RedirectComponent),
    canActivate: [initialGuard()],
  },
  {
    path: '',
    canActivate: [PermissionGuard],
    data: {
      permissions: [Permission.browse],
      routerLink: '/no-permission',
    },
    children: [
      {
        path: 'error',
        loadComponent: () =>
          import('./modules/cart/components/ordering/ordering-error/ordering-error.component').then(m => m.OrderingErrorComponent),
      },
      {
        path: 'success',
        loadComponent: () =>
          import('./modules/cart/components/ordering/ordering-success/ordering-success.component').then(m => m.OrderingSuccessComponent),
      },
      {
        path: '',
        component: LayoutComponent,
        children: [
          {
            path: '',
            loadChildren: () => import('./modules/home/home.module').then(m => m.HomeModule),
          },
          {
            path: 'offers',
            loadChildren: () => import('./modules/offers/offers.module').then(m => m.OffersModule),
          },
          {
            path: 'invoices',
            loadChildren: () => import('./modules/invoices/invoices.module').then(m => m.InvoicesModule),
          },
          {
            path: 'orders',
            loadChildren: () => import('./modules/orders/orders.module').then(m => m.OrdersModule),
          },
          {
            path: 'search',
            loadChildren: () => import('./modules/search-result/search-result.module').then(m => m.SearchResultModule),
          },
          {
            path: 'cart',
            loadChildren: () => import('./modules/cart/cart.module').then(m => m.CartModule),
          },
          {
            path: 'user-profile',
            loadChildren: () => import('./modules/user-profile/user-profile.module').then(m => m.UserProfileModule),
          },
          {
            path: 'category',
            loadChildren: () => import('./modules/categories-all/category.module').then(m => m.CategoryModule),
          },
          {
            path: 'balance-top-up',
            loadComponent: () =>
              import('./modules/payment/components/balance-top-up/balance-top-up.component').then(c => c.BalanceTopUpComponent),
            canActivate: [contentHiddenGuard([BuildType.ru, BuildType.kz])],
          },
          {
            path: 'mutual-settlements',
            loadChildren: () => import('./modules/mutual-settlements/mutual-settlements.module').then(m => m.MutualSettlementsModule),
          },
          {
            path: 'debts',
            loadChildren: () => import('./modules/debts/debts.module').then(m => m.DebtsModule),
          },
          {
            path: 'notifications',
            loadChildren: () => import('./modules/notifications/notifications.module').then(m => m.NotificationsModule),
          },
          {
            path: 'catalog/laximo',
            loadChildren: () => import('./modules/catalog/laximo/laximo.module').then(m => m.LaximoModule),
            canMatch: [() => inject(EnvironmentService).isAnyBuild([BuildType.ru, BuildType.kz])],
            canActivate: [contentHiddenGuard([BuildType.tr])],
          },
          {
            path: 'catalog',
            loadChildren: () => import('./modules/catalog/catalog.module').then(m => m.CatalogModule),
            canActivate: [contentHiddenGuard([BuildType.tr])],
          },
          {
            path: 'contacts',
            loadChildren: () => import('./modules/contacts/contacts.module').then(m => m.ContactsModule),
            canActivate: [contentHiddenGuard([BuildType.tr, BuildType.kz])],
          },
          {
            path: 'work-at-us',
            loadComponent: () => import('./pages/work-at-us/work-at-us.component').then(c => c.WorkAtUsComponent),
            canMatch: [() => inject(EnvironmentService).isBuildType(BuildType.ru)],
          },
          {
            path: 'request-history',
            loadChildren: () => import('./modules/request-history/request-history.module').then(c => c.RequestHistoryModule),
          },
          {
            path: 'token-history',
            loadComponent: () =>
              import('./modules/token-history/token-history.component').then(component => component.TokenHistoryComponent),
          },
          {
            path: 'refund',
            loadComponent: () => import('./pages/refund/refund.component').then(c => c.RefundComponent),
            canActivate: [contentHiddenGuard([BuildType.tr, BuildType.kz])],
          },
          {
            path: 'guarantee',
            loadComponent: () => import('./pages/guarantee/guarantee.component').then(c => c.GuaranteeComponent),
            canActivate: [contentHiddenGuard([BuildType.tr])],
          },
          {
            path: 'documents',
            loadComponent: () => import('./pages/documents/documents.component').then(c => c.DocumentsComponent),
            canActivate: [contentHiddenGuard([BuildType.tr])],
          },
          {
            path: 'promotion',
            loadChildren: () => import('./modules/promotion/promotion.module').then(m => m.PromotionModule),
          },
          {
            path: 'news',
            loadChildren: () => import('./modules/news/news.module').then(m => m.NewsModule),
          },
          {
            path: 'help-desk',
            loadComponent: () => import('./modules/help-desk/help-desk/help-desk.component').then(c => c.HelpDeskComponent),
          },
          {
            path: 'payment-details',
            loadComponent: () => import('./pages/payment-details/payment-details.component').then(c => c.PaymentDetailsComponent),
            canActivate: [contentHiddenGuard([BuildType.tr, BuildType.kz])],
          },
        ],
      },
    ],
  },
  {
    path: 'no-permission',
    loadComponent: () => import('./pages/no-permission/no-permission.component').then(c => c.NoPermissionComponent),
  },
  {
    path: 'personal-data-agreement',
    loadComponent: () =>
      import('./pages/personal-data-agreement/personal-data-agreement.component').then(c => c.PersonalDataAgreementComponent),
    canActivate: [contentHiddenGuard([BuildType.tr, BuildType.kz])],
  },
  {
    path: 'privacy-policy',
    canActivate: [contentHiddenGuard([BuildType.tr, BuildType.kz])],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./pages/privacy-policy/privacy-policy-list.component').then(c => c.PrivacyPolicyListComponent),
      },
      {
        path: 'other',
        loadComponent: () =>
          import('./pages/privacy-policy/privacy-policy-company-list.component').then(c => c.PrivacyPolicyCompanyListComponent),
      },
      {
        path: `:${PRIVACY_POLICY_SLUG_PARAM}`,
        loadComponent: () => import('./pages/privacy-policy/privacy-policy.component').then(c => c.PrivacyPolicyComponent),
      },
    ],
  },
  {
    path: 'payment-info',
    loadComponent: () => import('./pages/payment-info/payment-info.component').then(c => c.PaymentInfoComponent),
    canActivate: [contentHiddenGuard([BuildType.tr, BuildType.kz])],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
